import { useState, useEffect } from 'react';
import { FaTimes, FaSpinner, FaBuilding, FaChartBar } from 'react-icons/fa';
import { getCompanyGroup, CnpjSpotCompany, CnpjSpotGroupResponse, searchPartnerCompanies, PartnerCompanyResult } from '../../../services/CnpjSpotService';
import { getPafAnalysis, getBureauDetails, PafAnalysisData } from '../../../services/CreditAnalysisService';
import { unmaskDocument, isCpf, maskCnpj, maskDocument } from './utils/documentUtils';
import { fmtCurrency } from './utils/currencyUtils';
import { extractPfRegistration } from './utils/bureauUtils';
import PfRegistrationCard from './components/drawee/PfRegistrationCard';
import PartnerCompaniesTab from './components/drawee/PartnerCompaniesTab';

// ═══════════════════════════════════════════════════════════════
// CONSTANTS & VARIABLES
// ═══════════════════════════════════════════════════════════════

const pctFields = ['mgEbit', 'mgLiquida', 'roa', 'roe', 'divLiqrecLiq', 'endividamentoTotalpl', 'permanentepl', 'plat', 'arvendas', 'estoquesvendas'];

const BALANCE: Field[] = [
    { key: 'caixa', label: 'Caixa' },
    { key: 'ar', label: 'Contas à Receber' },
    { key: 'estoques', label: 'Estoques' },
    { key: '_occ_a', label: 'Outras Contas Circ.', calc: d => String(Math.round((parseFloat(d.ac || '0') || 0) - (parseFloat(d.caixa || '0') || 0) - (parseFloat(d.ar || '0') || 0) - (parseFloat(d.estoques || '0') || 0))) },
    { key: 'ac', label: 'Ativo Circulante', highlight: true },
    { key: '_onc_a', label: 'Outras Contas NC', calc: d => String(Math.round((parseFloat(d.at || '0') || 0) - (parseFloat(d.ac || '0') || 0) - (parseFloat(d.permanenteLiquido || '0') || 0))) },
    { key: 'permanenteLiquido', label: 'Permanente' },
    { key: 'at', label: 'Ativo Total', highlight: true },
];

const PASSIVO: Field[] = [
    { key: 'bancosCp', label: 'Bancos CP' },
    { key: 'forn', label: 'Fornecedores' },
    { key: '_occ_p', label: 'Outras Contas Circ.', calc: d => String(Math.round((parseFloat(d.pc || '0') || 0) - (parseFloat(d.bancosCp || '0') || 0) - (parseFloat(d.forn || '0') || 0))) },
    { key: 'pc', label: 'Passivo Circ.', highlight: true },
    { key: 'bancosLp', label: 'Bancos LP' },
    { key: '_onc_p', label: 'Outras Contas NC', calc: d => String(Math.round((parseFloat(d.at || '0') || 0) - (parseFloat(d.pc || '0') || 0) - (parseFloat(d.bancosLp || '0') || 0) - (parseFloat(d.pl || '0') || 0))) },
    { key: 'cs', label: 'Capital Social', highlight: true },
    { key: 'pl', label: 'Patrimônio Líquido', highlight: true },
    { key: 'at', label: 'Passivo Total', highlight: true },
];

const DRE: Field[] = [
    { key: 'recliq', label: 'Receita Líquida' },
    { key: 'ebit', label: 'EBIT', highlight: true },
    { key: 'mgEbit', label: 'Mg EBIT' },
    { key: 'resultLiq', label: 'Resultado Líq.', highlight: true },
    { key: 'mgLiquida', label: 'Mg Líquida' },
];

const INDICADORES: Field[] = [
    { key: 'divBrutaRmil', label: 'Dív. Bruta R$mil' },
    { key: 'divLiqRmil', label: 'Dív. Líq. R$mil' },
    { key: 'endividamentoTotalpl', label: 'Endivid. Total/PL' },
    { key: 'divLiqrecLiq', label: 'Dív Líq./Rec. Líq.' },
    { key: 'plat', label: 'AT/PL' },
    { key: 'permanentepl', label: 'Permanente/PL' },
    { key: 'liqCorrente', label: 'Liq. Corrente' },
    { key: 'arvendas', label: 'AR/Vendas' },
    { key: 'estoquesvendas', label: 'Estoques/Vendas' },
    { key: 'roa', label: 'ROA' },
    { key: 'roe', label: 'ROE' },
];

// ═══════════════════════════════════════════════════════════════
// INTERFACES & TYPES
// ═══════════════════════════════════════════════════════════════

interface Props {
    batchId: string;
    itemId: string;
    document: string;
    draweeName?: string;
    onClose: () => void;
}

type Tab = 'cadastro' | 'financeiro' | 'empresas';

type Field = { key: string; label: string; highlight?: boolean; calc?: (d: Record<string, string>) => string };

// ═══════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════



const renderField = (label: string, value: string | number | null | undefined) => (
    <div className="py-1.5 border-b border-gray-100 dark:border-slate-600">
        <div className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</div>
        <div className="text-sm text-gray-900 dark:text-gray-100">{value != null && value !== '' ? String(value) : <span className="text-gray-400 italic">-</span>}</div>
    </div>
);

function fmtValue(value: string | undefined, key: string): string {
    if (!value || value === '-') return '-';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    if (pctFields.includes(key)) return (num * 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 }) + '%';
    if (key === 'liqCorrente') return num.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + 'x';
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function calcLimits(data: Record<string, string>) {
    const divBruta = parseFloat(data.divBrutaRmil || '0') || 0;
    const recLiq = parseFloat(data.recliq || '0') || 0;
    const forn = parseFloat(data.forn || '0') || 0;
    const pl = parseFloat(data.pl || '0') || 0;
    return {
        lcCedente: Math.max(0, (divBruta * 0.10 + recLiq * 0.07 + forn * 0.10 + pl * 0.50) / 4),
        lcSacado: Math.max(0, (divBruta * 0.05 + recLiq * 0.05 + forn * 0.05 + pl * 0.05) / 4),
    };
}

function formatClosingDate(d: string | null): string {
    if (!d) return '-';
    const parts = d.split('-');
    return parts.length === 3 ? `${parts[1]}/${parts[0]}` : d;
}

// ═══════════════════════════════════════════════════════════════
// COMPONENTS
// ═══════════════════════════════════════════════════════════════

function CompanyCard({ company, badge, borderColor }: { company: CnpjSpotCompany; badge: string; borderColor: string }) {
    const endereco = [company.logradouro, company.numero, company.complemento].filter(Boolean).join(', ');
    const cidadeUf = [company.municipio, company.uf].filter(Boolean).join('/');
    return (
        <div className={`border-2 rounded p-4 ${borderColor}`}>
            <span className="text-xs font-bold uppercase mb-2 block">{badge}</span>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-0">
                {renderField('CNPJ', maskCnpj(company.cnpj))}
                {renderField('Razão Social', company.razao_social)}
                {renderField('Nome Fantasia', company.nome_fantasia)}
                {renderField('Situação', company.descricao_situacao_cadastral)}
                {renderField('Início Atividade', company.data_inicio_atividade)}
                {renderField('Capital Social', fmtCurrency(company.capital_social))}
                {renderField('Porte', company.porte)}
                {renderField('Natureza Jurídica', company.natureza_juridica)}
                {renderField('CNAE Principal', company.cnae_fiscal_descricao)}
                {renderField('Endereço', `${endereco}${company.bairro ? ` — ${company.bairro}` : ''}`)}
                {renderField('Cidade/UF', cidadeUf)}
                {renderField('CEP', company.cep)}
                {company.ddd_telefone_1 && renderField('Telefone', company.ddd_telefone_1)}
                {company.email && renderField('E-mail', company.email)}
            </div>
            {company.qsa && company.qsa.length > 0 && (
                <div className="mt-3">
                    <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase mb-1">Sócios ({company.qsa.length})</div>
                    <table className="w-full text-xs">
                        <thead><tr className="border-b text-gray-500 dark:text-gray-400">
                            <th className="text-left py-1">Nome</th><th className="text-left py-1">CPF/CNPJ</th><th className="text-left py-1">Qualificação</th>
                        </tr></thead>
                        <tbody>{company.qsa.map((s, i) => (
                            <tr key={i} className="border-b border-gray-100 dark:border-slate-600">
                                <td className="py-1 text-gray-900 dark:text-gray-100">{s.nome_socio}</td>
                                <td className="py-1 font-mono text-gray-700 dark:text-gray-300">{s.cnpj_cpf_do_socio || '-'}</td>
                                <td className="py-1 text-gray-700 dark:text-gray-300">{s.qualificacao_socio || '-'}</td>
                            </tr>
                        ))}</tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function FinTable({ title, fields, periods, zebra }: { title: string; fields: Field[]; periods: PafAnalysisData['periods']; zebra?: boolean }) {
    return (
        <div>
            <h4 className="font-semibold text-sm mb-1 text-gray-700 dark:text-gray-300">{title}</h4>
            <table className="w-full text-xs">
                <thead><tr className="border-b dark:border-gray-600">
                    <th className="text-left py-1 pr-2"></th>
                    {periods.map((p, i) => <th key={i} className="text-right py-1 px-1 whitespace-nowrap">{formatClosingDate(p.closingDate)}</th>)}
                </tr></thead>
                <tbody>{fields.map((f, idx) => (
                    <tr key={f.key} className={`border-b border-gray-100 dark:border-gray-700 ${f.highlight ? 'bg-gray-100 dark:bg-gray-700' : zebra && idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800' : ''}`}>
                        <td className={`py-0.5 pr-2 whitespace-nowrap ${f.highlight ? 'font-semibold text-gray-800 dark:text-gray-200' : 'text-gray-600 dark:text-gray-400'}`}>{f.label}</td>
                        {periods.map((p, i) => {
                            const val = f.calc ? f.calc(p.data) : p.data[f.key];
                            return <td key={i} className={`py-0.5 px-1 text-right font-mono ${f.highlight ? 'font-bold' : ''}`}>{fmtValue(val, f.key)}</td>;
                        })}
                    </tr>
                ))}</tbody>
            </table>
        </div>
    );
}

function FinancialTab({ paf }: { paf: PafAnalysisData }) {
    if (!paf.periods || paf.periods.length === 0) return <div className="p-8 text-center text-gray-500">Sem dados PAF disponíveis.</div>;
    const sortedPeriods = [...paf.periods].sort((a, b) => {
        const da = a.closingDate || '';
        const db = b.closingDate || '';
        if (!da && db) return 1;
        if (da && !db) return -1;
        return da.localeCompare(db);
    });
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm bg-gray-50 dark:bg-gray-900 rounded p-3">
                <div><span className="text-gray-500">Grupo:</span> {paf.generalData.economicGroup || '-'}</div>
                <div><span className="text-gray-500">Cidade:</span> {paf.generalData.city}/{paf.generalData.state}</div>
                <div><span className="text-gray-500">Rating:</span> <span className="font-bold">{paf.generalData.rating || '-'}</span></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FinTable title="ATIVO" fields={BALANCE} periods={sortedPeriods} />
                <FinTable title="PASSIVO" fields={PASSIVO} periods={sortedPeriods} />
                <div className="space-y-4">
                    <FinTable title="DRE" fields={DRE} periods={sortedPeriods} />
                    <div>
                        <h4 className="font-semibold text-sm mb-1 text-gray-700 dark:text-gray-300">LIMITES TEÓRICOS R$mil</h4>
                        <table className="w-full text-xs">
                            <thead><tr className="border-b dark:border-gray-600">
                                <th className="text-left py-1 pr-2"></th>
                                {sortedPeriods.map((p, i) => <th key={i} className="text-right py-1 px-1 whitespace-nowrap">{formatClosingDate(p.closingDate)}</th>)}
                            </tr></thead>
                            <tbody>
                                <tr className="border-b border-gray-100 dark:border-gray-700">
                                    <td className="py-0.5 pr-2 text-gray-600 dark:text-gray-400">LC Cedente</td>
                                    {sortedPeriods.map((p, i) => <td key={i} className="py-0.5 px-1 text-right font-mono">{calcLimits(p.data).lcCedente.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}</td>)}
                                </tr>
                                <tr className="border-b border-gray-100 dark:border-gray-700 bg-gray-100 dark:bg-gray-700">
                                    <td className="py-0.5 pr-2 font-semibold text-gray-800 dark:text-gray-200">LC Sacado</td>
                                    {sortedPeriods.map((p, i) => <td key={i} className="py-0.5 px-1 text-right font-mono font-bold">{calcLimits(p.data).lcSacado.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}</td>)}
                                </tr>
                                <tr className="border-b border-gray-100 dark:border-gray-700">
                                    <td className="py-0.5 pr-2 text-gray-600 dark:text-gray-400">Exposição Total</td>
                                    <td className="py-0.5 px-1 text-right font-mono" colSpan={sortedPeriods.length}>{paf.exposure != null ? paf.exposure.toLocaleString('pt-BR', { maximumFractionDigits: 0 }) : 'Sem Exposição'}</td>
                                </tr>
                                <tr className="border-b border-gray-100 dark:border-gray-700 bg-gray-100 dark:bg-gray-700">
                                    <td className="py-0.5 pr-2 font-semibold text-gray-800 dark:text-gray-200">% Exposição/LC Sacado</td>
                                    <td className="py-0.5 px-1 text-right font-mono font-bold" colSpan={sortedPeriods.length}>{(() => {
                                        if (!paf.exposure) return '-';
                                        const mostRecent = [...sortedPeriods].reverse().find(p => p.closingDate) || sortedPeriods[sortedPeriods.length - 1];
                                        const { lcSacado } = calcLimits(mostRecent.data);
                                        return lcSacado > 0 ? ((paf.exposure / lcSacado) * 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%' : '-';
                                    })()}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <FinTable title="INDICADORES" fields={INDICADORES} periods={sortedPeriods} zebra />
            </div>
            <div>
                <h4 className="font-semibold text-sm mb-2 text-gray-700 dark:text-gray-300">PARECER</h4>
                {paf.generalData.creditOpinion ? (
                    <div className="text-sm border-l-2 border-blue-400 pl-3 py-1"><p className="text-gray-700 dark:text-gray-300">{paf.generalData.creditOpinion}</p></div>
                ) : sortedPeriods.filter(p => p.data?.parecerDeCredito).length > 0 ? (
                    <div className="space-y-2">
                        {sortedPeriods.filter(p => p.data?.parecerDeCredito).map((p, i) => (
                            <div key={i} className="text-sm border-l-2 border-blue-400 pl-3 py-1">
                                <p className="text-gray-700 dark:text-gray-300">{p.data.parecerDeCredito}</p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 italic">Nenhum parecer disponível.</p>
                )}
            </div>
            {paf.references.length > 0 ? (
                <div>
                    <h4 className="font-semibold text-sm mb-2 text-gray-700 dark:text-gray-300">REFERÊNCIAS COMERCIAIS</h4>
                    <div className="space-y-2">
                        {paf.references.map((ref, i) => (
                            <div key={i} className="text-sm border-l-2 border-blue-400 pl-3 py-1">
                                <div className="flex gap-3 text-gray-500 text-xs"><span>{ref.date || '-'}</span><span className="font-medium">{ref.source}</span></div>
                                <p className="text-gray-700 dark:text-gray-300">{ref.comment}</p>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div>
                    <h4 className="font-semibold text-sm mb-2 text-gray-700 dark:text-gray-300">REFERÊNCIAS COMERCIAIS</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 italic">Nenhuma referência comercial disponível.</p>
                </div>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

export default function UnifiedDraweeModal({ batchId, itemId, document, draweeName, onClose }: Props) {
    const cleanDoc = unmaskDocument(document);
    const isCPF = isCpf(cleanDoc);
    const root = cleanDoc.substring(0, 8);

    const [tab, setTab] = useState<Tab>(isCPF ? 'cadastro' : 'financeiro');
    const [pjData, setPjData] = useState<CnpjSpotGroupResponse | null>(null);
    const [pafData, setPafData] = useState<PafAnalysisData | null>(null);
    const [pfRegistration, setPfRegistration] = useState<any>(null);
    const [partnerCompanies, setPartnerCompanies] = useState<PartnerCompanyResult[] | null>(null);
    const [loadingPartners, setLoadingPartners] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showBranches, setShowBranches] = useState(false);

    useEffect(() => {
        setLoading(true);
        if (isCPF) {
            getBureauDetails(itemId)
                .then(data => {
                    const result = extractPfRegistration(data.bureaus || []);
                    if (result) setPfRegistration(result);
                })
                .catch(() => { })
                .finally(() => setLoading(false));
        } else {
            Promise.all([
                getCompanyGroup(root).catch(() => null),
                getPafAnalysis(batchId, itemId).catch(() => null),
            ]).then(([pj, paf]) => {
                setPjData(pj);
                setPafData(paf);
            }).finally(() => setLoading(false));
        }
    }, [batchId, itemId, root, isCPF]);

    const currentBranch = pjData?.branches.find(b => unmaskDocument(b.cnpj) === cleanDoc);
    const isMatrix = pjData?.parent_company?.cnpj ? unmaskDocument(pjData.parent_company.cnpj) === cleanDoc : false;
    const otherBranches = pjData?.branches.filter(b => unmaskDocument(b.cnpj) !== cleanDoc) || [];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-[95vw] max-w-[1400px] max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700 flex-shrink-0">
                    <div>
                        <h2 className="text-lg font-bold dark:text-white">{draweeName || pafData?.generalData.name || pjData?.parent_company?.razao_social || pfRegistration?.consumerName || document}</h2>
                        <p className="text-sm text-gray-500 font-mono">{maskDocument(cleanDoc)} — {isCPF ? 'Pessoa Física' : isMatrix ? 'Matriz' : 'Filial'}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"><FaTimes size={18} /></button>
                </div>

                {/* Tabs */}
                <div className="flex border-b dark:border-gray-700 px-6 flex-shrink-0">
                    {!isCPF && (
                        <button onClick={() => setTab('financeiro')} className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === 'financeiro' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                            <FaChartBar className="inline mr-1.5" />Análise Financeira
                        </button>
                    )}
                    <button onClick={() => setTab('cadastro')} className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === 'cadastro' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                        <FaBuilding className="inline mr-1.5" />{isCPF ? 'Dados do Bureau' : 'Dados da Receita'}
                    </button>
                    {isCPF && (
                        <button onClick={() => {
                            setTab('empresas');
                            if (partnerCompanies === null) {
                                setLoadingPartners(true);
                                const firstName = (draweeName || pfRegistration?.consumerName || '').split(' ')[0];
                                searchPartnerCompanies(cleanDoc, firstName)
                                    .then(setPartnerCompanies)
                                    .catch(() => setPartnerCompanies([]))
                                    .finally(() => setLoadingPartners(false));
                            }
                        }} className={`px-4 py-2 text-sm font-medium border-b-2 ${tab === 'empresas' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                            <FaBuilding className="inline mr-1.5" />Empresas segundo a Receita
                        </button>
                    )}
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12"><FaSpinner className="animate-spin text-2xl text-blue-500" /></div>
                    ) : isCPF ? (
                        tab === 'empresas' ? (
                            <PartnerCompaniesTab
                                companies={partnerCompanies}
                                loading={loadingPartners}
                                cleanDoc={cleanDoc}
                            />
                        ) : (
                            <PfRegistrationCard registration={pfRegistration} cleanDoc={cleanDoc} />
                        )
                    ) : tab === 'cadastro' ? (
                        <div className="space-y-4">
                            {!isMatrix && currentBranch && (
                                <CompanyCard company={currentBranch} badge="Sacado (Filial)" borderColor="border-amber-400 dark:border-amber-500 bg-amber-50 dark:bg-slate-700" />
                            )}
                            {pjData?.parent_company && (
                                <CompanyCard company={pjData.parent_company} badge={isMatrix ? 'Sacado (Matriz)' : 'Matriz'} borderColor={isMatrix ? 'border-amber-400 dark:border-amber-500 bg-amber-50 dark:bg-slate-700' : 'border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-slate-700'} />
                            )}
                            {otherBranches.length > 0 && (
                                <div>
                                    <button onClick={() => setShowBranches(p => !p)} className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600">
                                        <span className={`transform transition-transform ${showBranches ? 'rotate-90' : ''}`}>▶</span>
                                        Outras Filiais ({otherBranches.length})
                                    </button>
                                    {showBranches && (
                                        <div className="mt-3 space-y-3">
                                            {otherBranches.map(b => <CompanyCard key={b.cnpj} company={b} badge="Filial" borderColor="border-gray-200 dark:border-slate-600" />)}
                                        </div>
                                    )}
                                </div>
                            )}
                            {!pjData && <div className="text-gray-500 text-center py-8">Dados cadastrais não disponíveis.</div>}
                        </div>
                    ) : (
                        pafData ? <FinancialTab paf={pafData} /> : <div className="text-gray-500 text-center py-8">Dados financeiros não disponíveis.</div>
                    )}
                </div>
            </div>
        </div>
    );
}
